import React, { useState, useEffect } from 'react';
import {
    Card,
    Button,
    Toast,
    Spin,
    Space,
    Typography,
    Select,
    Input,
    TextArea,
    Modal,
    Descriptions,
    Tag,
    Empty,
    Divider,
    Image,
    Avatar,
} from '@douyinfe/semi-ui';
import {
    IconRefresh,
    IconUpload,
    IconQrCode,
    IconInfoCircle,
    IconClock,
    IconSourceControl,
    IconUserAdd,
    IconAlertTriangle,
    IconVerify,
    IconEdit,
    IconCamera,
    IconPlus,
    IconSave,
    IconClose,
    IconList,
    IconDeleteStroked,
    IconChecklistStroked,
    IconCheckboxTick as IconConvert,
    IconSend,
    IconBackward,
    IconRedo,
    IconPlay
} from '@douyinfe/semi-icons';
import request from '../../utils/request';
import { useLocation } from 'react-router-dom';
import ImageSelector from '../../components/ImageSelector';
import styles from './index.module.scss';

const XcxVersionManager = () => {
    const [loading, setLoading] = useState(false);
    const [applets, setApplets] = useState([]);
    const [selectedApplet, setSelectedApplet] = useState(null);
    const [versionInfo, setVersionInfo] = useState(null);
    const [codeTemplates, setCodeTemplates] = useState([]);
    const [basicInfo, setBasicInfo] = useState(null);
    const [basicInfoLoading, setBasicInfoLoading] = useState(false);
    const [submitModalVisible, setSubmitModalVisible] = useState(false);
    const [submitForm, setSubmitForm] = useState({
        template_id: '',
        user_desc: '',
        user_version: '',
        ext_json: ''
    });
    const [submitLoading, setSubmitLoading] = useState(false);
    const [qrCodeModalVisible, setQrCodeModalVisible] = useState(false);
    const [qrCodeData, setQrCodeData] = useState(null);
    const [qrCodeLoading, setQrCodeLoading] = useState(false);
    const [currentQrCodeType, setCurrentQrCodeType] = useState('');
    const [testerModalVisible, setTesterModalVisible] = useState(false);
    const [testerForm, setTesterForm] = useState({
        wxid: ''
    });
    const [testerLoading, setTesterLoading] = useState(false);

    // 小程序转正相关状态
    const [betaConversionModalVisible, setBetaConversionModalVisible] = useState(false);
    const [betaConversionForm, setBetaConversionForm] = useState({
        enterprise_name: '',
        code_type: 1, // 默认统一社会信用代码
        code: '',
        legal_person_wechat: '',
        legal_person_name: '',
        legal_person_idcard: '',
        component_phone: ''
    });
    const [betaConversionLoading, setBetaConversionLoading] = useState(false);

    // 编辑相关状态
    const [nameEditModalVisible, setNameEditModalVisible] = useState(false);
    const [avatarEditModalVisible, setAvatarEditModalVisible] = useState(false);
    const [briefEditModalVisible, setBriefEditModalVisible] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [nameCheckLoading, setNameCheckLoading] = useState(false);

    // 编辑表单状态
    const [nameForm, setNameForm] = useState({
        name: '',
        license: '',
        id_card: '',
        naming_other_stuff_1: ''
    });
    const [briefForm, setBriefForm] = useState({ brief: '' });
    const [avatarForm, setAvatarForm] = useState({
        avatar: '',
        x1: '0',
        y1: '0',
        x2: '1',
        y2: '1'
    });
    const [nameCheckResult, setNameCheckResult] = useState(null);
    const [imageSelectorVisible, setImageSelectorVisible] = useState(false);

    // 类目相关状态
    const [categoryInfo, setCategoryInfo] = useState(null);
    const [categoryList, setCategoryList] = useState([]);
    const [categoryLoading, setCategoryLoading] = useState(false);
    const [categoryModalVisible, setCategoryModalVisible] = useState(false);
    const [categoryForm, setCategoryForm] = useState({
        first_id: '',
        second_id: ''
    });
    const [categoryActionLoading, setCategoryActionLoading] = useState(false);

    // 审核相关状态
    const [auditStatus, setAuditStatus] = useState(null);
    const [auditModalVisible, setAuditModalVisible] = useState(false);
    const [auditForm, setAuditForm] = useState({
        first_id: '',
        first_class: '',
        second_id: '',
        second_class: ''
    });
    const [auditLoading, setAuditLoading] = useState(false);
    const [auditStatusLoading, setAuditStatusLoading] = useState(false);

    const location = useLocation();

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const uid = queryParams.get('uid');
        if (uid) {
            fetchApplets(uid);
        } else {
            fetchApplets();
        }
    }, [location.search]);

    useEffect(() => {
        if (selectedApplet?.uid) {
            fetchVersionInfo(selectedApplet.uid);
            fetchCodeTemplates();
            fetchBasicInfo(selectedApplet.uid);
            fetchCategoryInfo(selectedApplet.uid);
            fetchCategoryList(selectedApplet.uid);
            fetchAuditStatus(selectedApplet.uid);
        }
    }, [selectedApplet]);

    const fetchApplets = async (uid = null) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/weixin/list_weixin_public/', {
                params: {
                    public_type: 8, // 微信小程序
                    limit: 100,
                    uids: uid
                }
            });
            if (response.errno === 0) {
                const fetchedApplets = response.list || [];
                setApplets(fetchedApplets);
                if (uid && fetchedApplets.length > 0) {
                    const preselectedApplet = fetchedApplets.find(item => item.uid == uid);
                    if (preselectedApplet) {
                        setSelectedApplet(preselectedApplet);
                    }
                } else if (!uid && fetchedApplets.length > 0) {
                    setSelectedApplet(fetchedApplets[0]);
                }
            } else {
                Toast.error(response.errstr || '获取小程序列表失败');
            }
        } catch (error) {
            console.error('获取小程序列表失败:', error);
            Toast.error('获取小程序列表失败');
        } finally {
            setLoading(false);
        }
    };

    const fetchVersionInfo = async (publicUid) => {
        try {
            const response = await request.get('/go/sp/wxc/version_info/', {
                params: { public_uid: publicUid }
            });
            if (response.errno === 0) {
                setVersionInfo(response);
            } else {
                Toast.error(response.errstr || '获取版本信息失败');
            }
        } catch (error) {
            console.error('获取版本信息失败:', error);
            Toast.error('获取版本信息失败');
        }
    };

    const fetchCodeTemplates = async () => {
        try {
            const response = await request.get('/go/sp/wxc/list_code_tpl/');
            if (response.errno === 0) {
                setCodeTemplates(response.list || []);
            } else {
                Toast.error(response.errstr || '获取代码列表失败');
            }
        } catch (error) {
            console.error('获取代码列表失败:', error);
            Toast.error('获取代码列表失败');
        }
    };

    const fetchBasicInfo = async (publicUid) => {
        setBasicInfoLoading(true);
        try {
            const response = await request.get('/go/sp/wxc/basic_info/', {
                params: { public_uid: publicUid }
            });
            if (response.errno === 0) {
                try {
                    const parsedInfo = JSON.parse(response.basic_info);
                    setBasicInfo(parsedInfo);
                } catch (parseError) {
                    console.error('解析基本信息失败:', parseError);
                    setBasicInfo(null);
                }
            } else {
                Toast.error(response.errstr || '获取基本信息失败');
                setBasicInfo(null);
            }
        } catch (error) {
            console.error('获取基本信息失败:', error);
            Toast.error('获取基本信息失败');
            setBasicInfo(null);
        } finally {
            setBasicInfoLoading(false);
        }
    };

    // 获取类目信息
    const fetchCategoryInfo = async (publicUid) => {
        try {
            const response = await request.get('/go/sp/wxc/category_info/', {
                params: { public_uid: publicUid }
            });
            if (response.errno === 0) {
                setCategoryInfo(response.category_info || []);
            } else {
                Toast.error(response.errstr || '获取类目信息失败');
                setCategoryInfo([]);
            }
        } catch (error) {
            console.error('获取类目信息失败:', error);
            Toast.error('获取类目信息失败');
            setCategoryInfo([]);
        }
    };

    // 获取可设置的类目列表
    const fetchCategoryList = async (publicUid) => {
        setCategoryLoading(true);
        try {
            const response = await request.get('/go/sp/wxc/list_xcx_category/', {
                params: { public_uid: publicUid }
            });
            if (response.errno === 0) {
                setCategoryList(response.list || []);
            } else {
                Toast.error(response.errstr || '获取类目列表失败');
                setCategoryList([]);
            }
        } catch (error) {
            console.error('获取类目列表失败:', error);
            Toast.error('获取类目列表失败');
            setCategoryList([]);
        } finally {
            setCategoryLoading(false);
        }
    };

    // 添加类目
    const addCategory = async () => {
        if (!categoryForm.first_id || !categoryForm.second_id) {
            Toast.error('请选择一级和二级类目');
            return;
        }

        setCategoryActionLoading(true);
        try {
            const response = await request.post('/go/sp/wxc/add_category/', {
                public_uid: selectedApplet.uid,
                first_id: categoryForm.first_id,
                second_id: categoryForm.second_id
            });

            if (response.errno === 0) {
                Toast.success('类目添加成功');
                setCategoryModalVisible(false);
                setCategoryForm({ first_id: '', second_id: '' });
                // 刷新类目信息
                fetchCategoryInfo(selectedApplet.uid);
            } else {
                Toast.error(response.errstr || '类目添加失败');
            }
        } catch (error) {
            console.error('类目添加失败:', error);
            Toast.error('类目添加失败');
        } finally {
            setCategoryActionLoading(false);
        }
    };

    // 删除类目
    const deleteCategory = async (firstId, secondId) => {
        setCategoryActionLoading(true);
        try {
            const response = await request.post('/go/sp/wxc/delete_category/', {
                public_uid: selectedApplet.uid,
                first_id: firstId,
                second_id: secondId
            });

            if (response.errno === 0) {
                Toast.success('类目删除成功');
                // 刷新类目信息
                fetchCategoryInfo(selectedApplet.uid);
            } else {
                Toast.error(response.errstr || '类目删除失败');
            }
        } catch (error) {
            console.error('类目删除失败:', error);
            Toast.error('类目删除失败');
        } finally {
            setCategoryActionLoading(false);
        }
    };

    // 获取审核状态
    const fetchAuditStatus = async (publicUid, auditId = '') => {
        setAuditStatusLoading(true);
        try {
            const response = await request.get('/go/sp/wxc/audit_status/', {
                params: {
                    public_uid: publicUid,
                    audit_id: auditId
                }
            });
            if (response.errno === 0) {
                setAuditStatus(response);
            } else {
                console.error('获取审核状态失败:', response.errstr);
                setAuditStatus(null);
            }
        } catch (error) {
            console.error('获取审核状态失败:', error);
            setAuditStatus(null);
        } finally {
            setAuditStatusLoading(false);
        }
    };

    // 提交审核
    const commitAudit = async () => {
        if (!auditForm.first_id || !auditForm.second_id) {
            Toast.error('请选择类目');
            return;
        }

        setAuditLoading(true);
        try {
            const response = await request.post('/go/sp/wxc/commit_audit/', {
                public_uid: selectedApplet.uid,
                first_id: auditForm.first_id,
                first_class: auditForm.first_class,
                second_id: auditForm.second_id,
                second_class: auditForm.second_class
            });

            if (response.errno === 0) {
                Toast.success('提交审核成功');
                setAuditModalVisible(false);
                setAuditForm({
                    first_id: '',
                    first_class: '',
                    second_id: '',
                    second_class: ''
                });
                // 刷新审核状态
                fetchAuditStatus(selectedApplet.uid);
            } else {
                Toast.error(response.errstr || '提交审核失败');
            }
        } catch (error) {
            console.error('提交审核失败:', error);
            Toast.error('提交审核失败');
        } finally {
            setAuditLoading(false);
        }
    };

    // 撤回审核
    const undoAudit = async () => {
        setAuditLoading(true);
        try {
            const response = await request.post('/go/sp/wxc/undo_audit/', {
                public_uid: selectedApplet.uid
            });

            if (response.errno === 0) {
                Toast.success('撤回审核成功');
                // 刷新审核状态
                fetchAuditStatus(selectedApplet.uid);
            } else {
                Toast.error(response.errstr || '撤回审核失败');
            }
        } catch (error) {
            console.error('撤回审核失败:', error);
            Toast.error('撤回审核失败');
        } finally {
            setAuditLoading(false);
        }
    };

    // 发布正式版
    const releaseVersion = async () => {
        setAuditLoading(true);
        try {
            const response = await request.post('/go/sp/wxc/release/', {
                public_uid: selectedApplet.uid
            });

            if (response.errno === 0) {
                Toast.success('发布成功');
                // 刷新版本信息和审核状态
                fetchVersionInfo(selectedApplet.uid);
                fetchAuditStatus(selectedApplet.uid);
            } else {
                Toast.error(response.errstr || '发布失败');
            }
        } catch (error) {
            console.error('发布失败:', error);
            Toast.error('发布失败');
        } finally {
            setAuditLoading(false);
        }
    };

    const handleAppletChange = (value) => {
        const applet = applets.find(item => item.uid === value);
        setSelectedApplet(applet);
    };

    const handleSubmitCode = async () => {
        if (!submitForm.template_id || !submitForm.user_desc || !submitForm.user_version) {
            Toast.error('请填写完整信息');
            return;
        }

        setSubmitLoading(true);
        try {
            const response = await request.post('/go/sp/wxc/commit_code/', {
                public_uid: selectedApplet.uid,
                template_id: submitForm.template_id,
                user_desc: submitForm.user_desc,
                user_version: submitForm.user_version,
                ext_json: submitForm.ext_json || undefined
            });

            if (response.errno === 0) {
                Toast.success('代码提交成功');
                setSubmitModalVisible(false);
                setSubmitForm({
                    template_id: '',
                    user_desc: '',
                    user_version: '',
                    ext_json: ''
                });
                // 刷新版本信息
                fetchVersionInfo(selectedApplet.uid);
            } else {
                Toast.error(response.errstr || '代码提交失败');
            }
        } catch (error) {
            console.error('代码提交失败:', error);
            Toast.error('代码提交失败');
        } finally {
            setSubmitLoading(false);
        }
    };

    const generateQrCode = async (envVersion) => {
        if (!selectedApplet?.uid) {
            Toast.error('请先选择小程序');
            return;
        }

        setQrCodeLoading(true);
        setCurrentQrCodeType(envVersion);

        try {
            const response = await request.get('/go/sp/weixin/xcx_qrcode/', {
                params: {
                    uid: selectedApplet.uid,
                    path: '', // 默认首页
                    env_version: envVersion, // release, trial, develop
                    is_hyaline: 0, // 不透明背景
                    width: 430 // 默认宽度
                },
                responseType: 'blob' // 接收图片数据
            });

            // 创建blob URL来显示图片
            const blob = new Blob([response], { type: 'image/png' });
            const imageUrl = URL.createObjectURL(blob);
            setQrCodeData(imageUrl);
            setQrCodeModalVisible(true);
        } catch (error) {
            console.error('生成二维码失败:', error);
            Toast.error('生成二维码失败');
        } finally {
            setQrCodeLoading(false);
        }
    };

    const closeQrCodeModal = () => {
        setQrCodeModalVisible(false);
        setQrCodeData(null);
        // 清理blob URL
        if (qrCodeData) {
            URL.revokeObjectURL(qrCodeData);
        }
    };

    const handleBindTester = async () => {
        if (!testerForm.wxid.trim()) {
            Toast.error('请输入微信号');
            return;
        }

        setTesterLoading(true);
        try {
            const response = await request.post('/go/sp/wxc/bind_tester/', {
                public_uid: selectedApplet.uid,
                wxid: testerForm.wxid.trim()
            });

            if (response.errno === 0) {
                Toast.success('添加体验者成功');
                setTesterModalVisible(false);
                setTesterForm({ wxid: '' });
            } else {
                Toast.error(response.errstr || '添加体验者失败');
            }
        } catch (error) {
            console.error('添加体验者失败:', error);
            Toast.error('添加体验者失败');
        } finally {
            setTesterLoading(false);
        }
    };

    // 小程序转正
    const handleCommitBeta = async () => {
        // 验证必填字段
        if (!betaConversionForm.enterprise_name.trim()) {
            Toast.error('请输入企业名称');
            return;
        }
        if (!betaConversionForm.code.trim()) {
            Toast.error('请输入企业代码');
            return;
        }
        if (!betaConversionForm.legal_person_wechat.trim()) {
            Toast.error('请输入法人微信');
            return;
        }
        if (!betaConversionForm.legal_person_name.trim()) {
            Toast.error('请输入法人姓名');
            return;
        }
        if (!betaConversionForm.legal_person_idcard.trim()) {
            Toast.error('请输入法人身份证');
            return;
        }
        if (!betaConversionForm.component_phone.trim()) {
            Toast.error('请输入联系电话');
            return;
        }

        setBetaConversionLoading(true);
        try {
            const response = await request.post('/go/sp/wxc/commit_beta/', {
                public_uid: selectedApplet.uid,
                enterprise_name: betaConversionForm.enterprise_name.trim(),
                code_type: betaConversionForm.code_type,
                code: betaConversionForm.code.trim(),
                legal_person_wechat: betaConversionForm.legal_person_wechat.trim(),
                legal_person_name: betaConversionForm.legal_person_name.trim(),
                legal_person_idcard: betaConversionForm.legal_person_idcard.trim(),
                component_phone: betaConversionForm.component_phone.trim()
            });

            if (response.errno === 0) {
                Toast.success('小程序转正申请提交成功，法人将收到扫脸验证通知');
                setBetaConversionModalVisible(false);
                setBetaConversionForm({
                    enterprise_name: '',
                    code_type: 1,
                    code: '',
                    legal_person_wechat: '',
                    legal_person_name: '',
                    legal_person_idcard: '',
                    component_phone: ''
                });
                // 刷新基本信息
                fetchBasicInfo(selectedApplet.uid);
            } else {
                Toast.error(response.errstr || '小程序转正申请失败');
            }
        } catch (error) {
            console.error('小程序转正申请失败:', error);
            Toast.error('小程序转正申请失败');
        } finally {
            setBetaConversionLoading(false);
        }
    };

    // 检查小程序名称
    const checkXcxName = async (name) => {
        /*
        if (!name || name.length < 4 || name.length > 30) {
            Toast.error('名称长度应在4-30个字符之间');
            return false;
        }
        */

        setNameCheckLoading(true);
        try {
            const response = await request.post('/go/sp/wxc/check_xcx_name/', {
                public_uid: selectedApplet.uid,
                name: name
            });

            if (response.errno === 0) {
                setNameCheckResult(response);
                return true;
            } else {
                Toast.error(response.errstr || '名称检查失败');
                setNameCheckResult(null);
                return false;
            }
        } catch (error) {
            console.error('名称检查失败:', error);
            Toast.error('名称检查失败');
            setNameCheckResult(null);
            return false;
        } finally {
            setNameCheckLoading(false);
        }
    };

    // 设置小程序名称
    const setXcxName = async () => {
        if (!nameForm.name.trim()) {
            Toast.error('请输入名称');
            return;
        }

        // 检查是否需要补充材料
        if (nameCheckResult?.hit_condition === 1) {
            // 企业类型需要营业执照
            if (basicInfo?.principal_type === 1 && !nameForm.license) {
                Toast.error('企业类型必须上传营业执照');
                return;
            }
            // 个人类型需要身份证
            if (basicInfo?.principal_type === 0 && !nameForm.id_card) {
                Toast.error('个人类型必须上传身份证');
                return;
            }
        }

        setEditLoading(true);
        try {
            const response = await request.post('/go/sp/wxc/set_xcx_name/', {
                public_uid: selectedApplet.uid,
                name: nameForm.name.trim(),
                license: nameForm.license,
                id_card: nameForm.id_card,
                naming_other_stuff_1: nameForm.naming_other_stuff_1
            });

            if (response.errno === 0) {
                Toast.success('名称设置成功');
                setNameEditModalVisible(false);
                setNameForm({ name: '', license: '', id_card: '', naming_other_stuff_1: '' });
                setNameCheckResult(null);
                // 刷新基本信息
                fetchBasicInfo(selectedApplet.uid);
            } else {
                Toast.error(response.errstr || '名称设置失败');
            }
        } catch (error) {
            console.error('名称设置失败:', error);
            Toast.error('名称设置失败');
        } finally {
            setEditLoading(false);
        }
    };

    // 设置小程序简介
    const setXcxBrief = async () => {
        if (!briefForm.brief.trim()) {
            Toast.error('请输入简介');
            return;
        }

        if (briefForm.brief.trim().length < 4 || briefForm.brief.trim().length > 120) {
            Toast.error('简介长度应在4-120个字符之间');
            return;
        }

        setEditLoading(true);
        try {
            const response = await request.post('/go/sp/wxc/set_xcx_brief/', {
                public_uid: selectedApplet.uid,
                brief: briefForm.brief.trim()
            });

            if (response.errno === 0) {
                Toast.success('简介设置成功');
                setBriefEditModalVisible(false);
                setBriefForm({ brief: '' });
                // 刷新基本信息
                fetchBasicInfo(selectedApplet.uid);
            } else {
                Toast.error(response.errstr || '简介设置失败');
            }
        } catch (error) {
            console.error('简介设置失败:', error);
            Toast.error('简介设置失败');
        } finally {
            setEditLoading(false);
        }
    };

    // 设置小程序头像
    const setXcxAvatar = async () => {
        if (!avatarForm.avatar) {
            Toast.error('请先上传头像');
            return;
        }

        setEditLoading(true);
        try {
            const response = await request.post('/go/sp/wxc/set_xcx_avatar/', {
                public_uid: selectedApplet.uid,
                avatar: avatarForm.avatar,
                x1: avatarForm.x1,
                y1: avatarForm.y1,
                x2: avatarForm.x2,
                y2: avatarForm.y2
            });

            if (response.errno === 0) {
                Toast.success('头像设置成功');
                setAvatarEditModalVisible(false);
                setAvatarForm({ avatar: '', x1: '0', y1: '0', x2: '1', y2: '1' });
                // 刷新基本信息
                fetchBasicInfo(selectedApplet.uid);
            } else {
                Toast.error(response.errstr || '头像设置失败');
            }
        } catch (error) {
            console.error('头像设置失败:', error);
            Toast.error('头像设置失败');
        } finally {
            setEditLoading(false);
        }
    };

    // 打开编辑弹窗并初始化数据
    const openNameEditModal = () => {
        setNameForm({
            name: basicInfo?.nickname || '',
            license: '',
            id_card: '',
            naming_other_stuff_1: ''
        });
        setNameCheckResult(null);
        setNameEditModalVisible(true);
    };

    const openBriefEditModal = () => {
        setBriefForm({
            brief: basicInfo?.signature_info?.signature || ''
        });
        setBriefEditModalVisible(true);
    };

    const openAvatarEditModal = () => {
        setAvatarForm({
            avatar: '',
            x1: '0',
            y1: '0',
            x2: '1',
            y2: '1'
        });
        setAvatarEditModalVisible(true);
    };

    // 检查是否为最新版本
    const isLatestVersion = (versionType) => {
        if (!versionInfo || !codeTemplates.length) return false;

        const currentVersion = versionType === 'trial'
            ? versionInfo.exp_info?.exp_version
            : versionInfo.release_info?.release_version;

        if (!currentVersion) return false;

        // 比较版本号，这里假设版本号格式为 x.y.z
        const currentParts = currentVersion.split('.').map(Number);
        const latestTemplate = codeTemplates[0]; // 假设按版本号排序，第一个是最新的
        const latestParts = latestTemplate.UserVersion.split('.').map(Number);

        for (let i = 0; i < Math.max(currentParts.length, latestParts.length); i++) {
            const current = currentParts[i] || 0;
            const latest = latestParts[i] || 0;
            if (current < latest) return false;
            if (current > latest) return true;
        }
        return true;
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '-';
        return new Date(timestamp * 1000).toLocaleString('zh-CN');
    };

    const getAccountTypeText = (type) => {
        const types = {
            1: '订阅号',
            2: '服务号',
            3: '小程序'
        };
        return types[type] || '未知';
    };

    const getPrincipalTypeText = (type) => {
        const types = {
            0: '个人',
            1: '企业',
            2: '媒体',
            3: '政府',
            4: '其他组织'
        };
        return types[type] || '未知';
    };

    // 判断是否为试用小程序
    const isTrialMiniprogram = (applet) => {
        if (!basicInfo || !applet || !applet.public_name) return false;
        return basicInfo.principal_type === 0 && applet.public_name.endsWith('的试用小程序');
    };

    // 计算试用截止时间（创建时间后2周）
    const getTrialExpiryDate = (createTime) => {
        if (!createTime) return null;
        const expiryDate = new Date(parseInt(createTime));
        expiryDate.setDate(expiryDate.getDate() + 14); // 加14天
        return expiryDate;
    };

    // 判断试用是否已过期或即将过期
    const getTrialStatus = (createTime) => {
        const expiryDate = getTrialExpiryDate(createTime);
        if (!expiryDate) return { status: 'unknown', daysLeft: 0 };

        const now = new Date();
        const daysLeft = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

        if (daysLeft <= 0) {
            return { status: 'expired', daysLeft: 0 };
        } else if (daysLeft <= 3) {
            return { status: 'warning', daysLeft };
        } else {
            return { status: 'normal', daysLeft };
        }
    };

    const getRealnameStatusText = (status) => {
        const statuses = {
            0: '未实名认证',
            1: '实名认证中',
            2: '实名认证成功',
            3: '实名认证失败'
        };
        return statuses[status] || '未知';
    };

    const getCustomerTypeText = (type) => {
        const types = {
            0: '-',
            1: '企业认证',
            12: '个体工商户认证',
            15: '个人认证'
        };
        return types[type] || '未知';
    };

    const getCategoryAuditStatusText = (status) => {
        const statuses = {
            1: '审核中',
            2: '审核不通过',
            3: '审核通过'
        };
        return statuses[status] || '未知';
    };

    const getCategoryAuditStatusColor = (status) => {
        const colors = {
            1: 'orange',    // 审核中
            2: 'red',       // 审核不通过
            3: 'green'      // 审核通过
        };
        return colors[status] || 'grey';
    };

    // 获取审核状态文本
    const getAuditStatusText = (status) => {
        const statuses = {
            0: '审核成功',
            1: '审核被拒绝',
            2: '审核中',
            3: '审核已撤回',
            4: '审核延后'
        };
        return statuses[status] || '未知状态';
    };

    // 获取审核状态颜色
    const getAuditStatusColor = (status) => {
        const colors = {
            0: 'green',     // 审核成功
            1: 'red',       // 审核被拒绝
            2: 'orange',    // 审核中
            3: 'grey',      // 审核已撤回
            4: 'yellow'     // 审核延后
        };
        return colors[status] || 'grey';
    };

    // 检查是否需要显示提交审核按钮
    const shouldShowSubmitAudit = () => {
        // 没有正式版或正式版跟体验版不一样的时候显示提交审核功能
        if (!versionInfo) return false;

        const hasReleaseVersion = versionInfo.release_info?.release_version;
        const hasTrialVersion = versionInfo.exp_info?.exp_version;

        // 没有正式版
        if (!hasReleaseVersion && hasTrialVersion) {
            return true;
        }

        // 有正式版但与体验版不一样
        if (hasReleaseVersion && hasTrialVersion) {
            const releaseVersion = versionInfo.release_info.release_version;
            const trialVersion = versionInfo.exp_info.exp_version;
            return releaseVersion !== trialVersion;
        }

        return false;
    };

    // 获取一级类目的二级类目列表
    const getSecondCategories = (firstCategoryId) => {
        const firstCategory = categoryList.find(cat => cat.id === parseInt(firstCategoryId));
        if (!firstCategory || !firstCategory.children) return [];

        return firstCategory.children.map(childId => {
            return categoryList.find(cat => cat.id === childId);
        }).filter(Boolean);
    };

    // 获取一级类目列表
    const getFirstCategories = () => {
        return categoryList.filter(cat => cat.level === 1);
    };

    // 渲染试用小程序提醒
    const renderTrialReminder = () => {
        if (!selectedApplet || !isTrialMiniprogram(selectedApplet)) return null;

        const trialStatus = getTrialStatus(selectedApplet.create_time);
        const expiryDate = getTrialExpiryDate(selectedApplet.create_time);

        let color = 'blue';
        let icon = <IconClock />;
        let title = '试用小程序提醒';

        if (trialStatus.status === 'expired') {
            color = 'red';
            icon = <IconAlertTriangle />;
            title = '试用已过期';
        } else if (trialStatus.status === 'warning') {
            color = 'orange';
            icon = <IconAlertTriangle />;
            title = '试用即将过期';
        }

        return (
            <Card className={`${styles.card} ${styles.trialReminderCard}`} style={{ marginBottom: '20px' }}>
                <div style={{
                    padding: '16px',
                    borderRadius: '8px',
                    backgroundColor: color === 'red' ? '#fff2f0' : color === 'orange' ? '#fff7e6' : '#e6f7ff',
                    border: `1px solid ${color === 'red' ? '#ffccc7' : color === 'orange' ? '#ffd591' : '#91d5ff'}`
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                        <span style={{ color: color === 'red' ? '#ff4d4f' : color === 'orange' ? '#fa8c16' : '#1890ff', marginRight: '8px' }}>
                            {icon}
                        </span>
                        <Typography.Title heading={6} style={{ margin: 0, color: color === 'red' ? '#ff4d4f' : color === 'orange' ? '#fa8c16' : '#1890ff' }}>
                            {title}
                        </Typography.Title>
                    </div>

                    <Space vertical spacing="small" style={{ width: '100%' }}>
                        <Typography.Text>
                            这是一个试用小程序，需要转正后才能正常发布和运营。
                        </Typography.Text>

                        {expiryDate && (
                            <Typography.Text type="secondary">
                                试用截止时间：{expiryDate.toLocaleDateString('zh-CN')} {expiryDate.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}
                            </Typography.Text>
                        )}

                        {trialStatus.status === 'expired' ? (
                            <Typography.Text strong style={{ color: '#ff4d4f' }}>
                                试用期已过期，小程序可能已被注销，请尽快联系管理员处理。
                            </Typography.Text>
                        ) : trialStatus.status === 'warning' ? (
                            <Typography.Text strong style={{ color: '#fa8c16' }}>
                                试用期还剩 {trialStatus.daysLeft} 天，请尽快转正，否则将被注销。
                            </Typography.Text>
                        ) : (
                            <Typography.Text>
                                试用期还剩 {trialStatus.daysLeft} 天，建议提前进行转正操作。
                            </Typography.Text>
                        )}

                        <div style={{ marginTop: '12px' }}>
                            <Button
                                type="primary"
                                icon={<IconConvert />}
                                onClick={() => setBetaConversionModalVisible(true)}
                                size="small"
                            >
                                立即转正
                            </Button>
                        </div>
                    </Space>
                </div>
            </Card>
        );
    };


    const renderBasicInfo = () => {
        if (!basicInfo) return null;

        return (
            <Card
                className={`${styles.card} ${styles.basicInfoCard}`}
            >
                {basicInfoLoading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <Space vertical spacing="loose" style={{ width: '100%' }}>
                        {/* 头像、名称和简介 */}
                        <div className={styles.basicHeader}>
                            <div className={styles.avatarSection}>
                                <Avatar
                                    size="large"
                                    src={basicInfo.head_image_info?.head_image_url}
                                    style={{ cursor: 'pointer' }}
                                    onClick={openAvatarEditModal}
                                >
                                    {basicInfo.nickname?.charAt(0) || '小'}
                                </Avatar>
                            </div>
                            <div className={styles.basicTitle}>
                                <div className={styles.nameRow}>
                                    <Typography.Title
                                        heading={4}
                                        style={{ margin: 0, cursor: 'pointer' }}
                                        onClick={openNameEditModal}
                                    >
                                        {basicInfo.nickname || '未设置名称'}
                                    </Typography.Title>
                                    {/* 微信认证信息和类目 */}
                                    <div className={styles.authTags}>
                                        <Tag
                                            size="small"
                                        >
                                            {getPrincipalTypeText(basicInfo.principal_type)}
                                        </Tag>

                                        {basicInfo.wx_verify_info?.qualification_verify ? (
                                            <Tag
                                                color="green"
                                                size="small"
                                                prefixIcon={<IconVerify />}
                                            >
                                                已认证
                                            </Tag>
                                        ) : (
                                            <Tag
                                                color="orange"
                                                size="small"
                                                prefixIcon={<IconAlertTriangle />}
                                            >
                                                未认证
                                            </Tag>
                                        )}
                                        {basicInfo.wx_verify_info?.naming_verify ? (
                                            <Tag
                                                color="blue"
                                                size="small"
                                                prefixIcon={<IconVerify />}
                                            >
                                                已实名认证
                                            </Tag>
                                        ) : (
                                            <Tag
                                                color="orange"
                                                size="small"
                                                prefixIcon={<IconAlertTriangle />}
                                            >
                                                未实名认证
                                            </Tag>
                                        )}

                                        {/* 类目标签 */}
                                        <div className={styles.categorySection}>
                                            {categoryInfo && categoryInfo.length > 0 ? (
                                                <>
                                                    {categoryInfo.map((category, index) => (
                                                        <Tag
                                                            key={index}
                                                            color={getCategoryAuditStatusColor(category.audit_status)}
                                                            size="small"
                                                            closable
                                                            onClose={(c, e) => {
                                                                console.log('on close', e, c)
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                return new Promise((resolve, reject) => {
                                                                    Modal.confirm({
                                                                        title: '确认删除类目',
                                                                        content: `确定要删除类目"${category.first_name} > ${category.second_name}"吗？`,
                                                                        onOk: async () => {
                                                                            try {
                                                                                await deleteCategory(category.first, category.second);
                                                                                resolve();
                                                                            } catch (error) {
                                                                                reject(error);
                                                                            }
                                                                        },
                                                                        onCancel: () => {
                                                                            resolve(false); // 取消时返回 false 而不是 reject
                                                                        }
                                                                    });
                                                                });
                                                            }}
                                                            className={styles.categoryTag}
                                                        >
                                                            {category.first_name} {' > '} {category.second_name}
                                                        </Tag>
                                                    ))}
                                                </>
                                            ) : (
                                                <Tag
                                                    color="orange"
                                                    size="small"
                                                    prefixIcon={<IconAlertTriangle />}
                                                >
                                                    未设置类目
                                                </Tag>
                                            )}

                                            {/* 添加类目按钮 */}
                                            <Button
                                                type="tertiary"
                                                theme="borderless"
                                                size="small"
                                                icon={<IconPlus />}
                                                onClick={() => setCategoryModalVisible(true)}
                                                disabled={categoryActionLoading}
                                                className={styles.addCategoryBtn}
                                            >
                                                添加类目
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                                <Typography.Text type="secondary">
                                    {basicInfo.appid || ''}
                                </Typography.Text>
                                <Typography.Text type="secondary">
                                    {basicInfo.principal_name || ''}
                                </Typography.Text>

                                {/* 功能介绍 */}
                                <div className={styles.briefSection}>
                                    <Typography.Text
                                        type="secondary"
                                        style={{
                                            cursor: 'pointer',
                                            display: 'block',
                                            marginTop: '8px',
                                            padding: '8px',
                                            backgroundColor: 'var(--semi-color-fill-0)',
                                            borderRadius: '6px',
                                            border: '1px dashed var(--semi-color-border)',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onClick={openBriefEditModal}
                                        className={styles.briefText}
                                    >
                                        {basicInfo.signature_info?.signature || '点击设置功能介绍'}
                                    </Typography.Text>
                                </div>
                            </div>
                        </div>
                    </Space>
                )}
            </Card>
        );
    };

    const renderVersionInfo = () => {
        if (!versionInfo) return null;

        return (
            <Card title="当前版本" className={`${styles.card} ${styles.versionInfoCard}`}>
                <Space vertical align="start" spacing="loose" style={{ width: '100%' }}>
                    <div className={styles.versionSection}>
                        <div className={styles.versionBlock}>
                            <div className={styles.versionHeader}>
                                <Typography.Title heading={6} className={styles.versionTitle}>
                                    <IconClock className={styles.icon} />
                                    体验版
                                </Typography.Title>
                                <div className={styles.versionActions}>
                                    {versionInfo.exp_info?.exp_version && (
                                        <Button
                                            type="tertiary"
                                            size="small"
                                            icon={<IconQrCode />}
                                            loading={qrCodeLoading && currentQrCodeType === 'trial'}
                                            onClick={() => generateQrCode('trial')}
                                            className={styles.qrCodeBtn}
                                        >
                                        </Button>
                                    )}
                                    {isLatestVersion('trial') && (
                                        <Tag color="green" size="small">
                                            已是最新版
                                        </Tag>
                                    )}
                                </div>
                            </div>
                            {versionInfo.exp_info ? (
                                <Descriptions data={[
                                    { key: '版本号', value: versionInfo.exp_info.exp_version || '-' },
                                    { key: '发布时间', value: formatTime(versionInfo.exp_info.exp_time) },
                                    { key: '版本描述', value: versionInfo.exp_info.exp_desc || '-' }
                                ]} size="small" />
                            ) : (
                                <Empty description="暂无体验版信息" image={<IconInfoCircle size="large" />} />
                            )}
                        </div>

                        <div className={styles.versionBlock}>
                            <div className={styles.versionHeader}>
                                <Typography.Title heading={6} className={styles.versionTitle}>
                                    <IconSourceControl className={styles.icon} />
                                    正式版
                                </Typography.Title>
                                <div className={styles.versionActions}>
                                    {versionInfo.release_info?.release_version && (
                                        <Button
                                            type="tertiary"
                                            size="small"
                                            icon={<IconQrCode />}
                                            loading={qrCodeLoading && currentQrCodeType === 'release'}
                                            onClick={() => generateQrCode('release')}
                                            className={styles.qrCodeBtn}
                                        >
                                        </Button>
                                    )}
                                    {isLatestVersion('release') && (
                                        <Tag color="green" size="small">
                                            已是最新版
                                        </Tag>
                                    )}
                                </div>
                            </div>


                            {shouldShowSubmitAudit() && (
                                <div className={styles.auditSection}>
                                    {auditStatus ? (
                                        <div className={styles.auditContent}>
                                            <div style={{ marginBottom: '16px' }}>
                                                <Tag
                                                    color={getAuditStatusColor(auditStatus.status)}
                                                    size="medium"
                                                >
                                                    {getAuditStatusText(auditStatus.status)}
                                                </Tag>
                                                {auditStatus.reason && (
                                                    <Typography.Text type="secondary" size="small" style={{ marginLeft: '12px' }}>
                                                        原因：{auditStatus.reason}
                                                    </Typography.Text>
                                                )}
                                            </div>

                                            <div className={styles.auditActions}>
                                                {auditStatus.status === 2 && ( // 审核中
                                                    <Button
                                                        type="secondary"
                                                        size="small"
                                                        icon={<IconBackward />}
                                                        loading={auditLoading}
                                                        onClick={() => {
                                                            Modal.confirm({
                                                                title: '确认撤回审核',
                                                                content: '确定要撤回当前审核吗？',
                                                                onOk: undoAudit
                                                            });
                                                        }}
                                                    >
                                                        撤回审核
                                                    </Button>
                                                )}

                                                {auditStatus.status === 0 && ( // 审核成功
                                                    <Button
                                                        type="primary"
                                                        size="small"
                                                        icon={<IconPlay />}
                                                        loading={auditLoading}
                                                        onClick={() => {
                                                            Modal.confirm({
                                                                title: '确认发布',
                                                                content: '确定要发布此版本为正式版吗？',
                                                                onOk: releaseVersion
                                                            });
                                                        }}
                                                    >
                                                        发布正式版
                                                    </Button>
                                                )}

                                                {(auditStatus.status === 1 || auditStatus.status === 3) && ( // 被拒绝或已撤回
                                                    <Button
                                                        type="primary"
                                                        size="small"
                                                        icon={<IconRedo />}
                                                        onClick={() => setAuditModalVisible(true)}
                                                    >
                                                        重新提交审核
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={styles.auditContent}>
                                            <div className={styles.noAuditInfo}>
                                                <Typography.Text type="secondary">
                                                    体验版与正式版不同步，可以提交审核发布新版本
                                                </Typography.Text>
                                            </div>
                                            <Button
                                                type="primary"
                                                size="small"
                                                icon={<IconSend />}
                                                onClick={() => setAuditModalVisible(true)}
                                            >
                                                提交审核
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {versionInfo.release_info ? (
                                <Descriptions data={[
                                    { key: '版本号', value: versionInfo.release_info.release_version || '-' },
                                    { key: '发布时间', value: formatTime(versionInfo.release_info.release_time) },
                                    { key: '版本描述', value: versionInfo.release_info.release_desc || '-' }
                                ]} size="small" />
                            ) : (
                                <Empty description="暂无正式版信息" image={<IconInfoCircle size="large" />} />
                            )}
                        </div>
                    </div>
                </Space>
            </Card>
        );
    };

    const renderCodeTemplates = () => {
        if (codeTemplates.length === 0) {
            return (
                <Card title="可用版本" style={{ marginBottom: '20px' }}>
                    <Empty description="暂无版本" />
                </Card>
            );
        }

        return (
            <Card title="可用版本" className={`${styles.card} ${styles.templateCard}`}>
                <Space vertical spacing="medium" style={{ width: '100%' }}>
                    {codeTemplates.map((template, index) => (
                        <Card key={template.TemplateId} size="small" className={styles.templateItem}>
                            <div className={styles.templateHeader}>
                                <div className={styles.templateInfo}>
                                    <Typography.Title heading={6} className={styles.templateTitle}>
                                        版本: {template.UserVersion}
                                    </Typography.Title>
                                    <div className={styles.templateTags}>
                                        <Tag color="green" size="small" className={styles.tag}>
                                            {template.UserDesc}
                                        </Tag>
                                    </div>
                                    {template.ExtJson && (
                                        <Typography.Text type="secondary" size="small" className={styles.templateExtInfo}>
                                            补充信息: {template.ExtJson}
                                        </Typography.Text>
                                    )}
                                </div>
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<IconUpload />}
                                    className={styles.templateAction}
                                    onClick={() => {
                                        setSubmitForm({
                                            template_id: template.TemplateId,
                                            user_desc: template.UserDesc,
                                            user_version: template.UserVersion,
                                            ext_json: template.ExtJson || ''
                                        });
                                        setSubmitModalVisible(true);
                                    }}
                                >
                                    使用此版本
                                </Button>
                            </div>
                        </Card>
                    ))}
                </Space>
            </Card>
        );
    };

    return (
        <div className={styles.container}>
            <Typography.Title heading={3} className={styles.title}>小程序版本管理</Typography.Title>

            <div className={styles.content}>
                {
                    applets && applets.length > 1 &&
                    <Card title="选择小程序" className={styles.card}>
                        <div className="semi-form-field semi-form-field-select semi-form-field-required">
                            <div className="semi-form-field-label">
                                <label className="semi-form-field-label-text">小程序</label>
                            </div>
                            <Select
                                placeholder="请选择小程序"
                                loading={loading}
                                onChange={handleAppletChange}
                                value={selectedApplet?.uid}
                                style={{ width: '100%' }}
                            >
                                {applets.map(applet => (
                                    <Select.Option key={applet.uid} value={applet.uid}>
                                        {applet.public_name} ({applet.app_id})
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                    </Card>
                }

                {/* 试用小程序提醒 */}
                {selectedApplet && renderTrialReminder()}

                {/* 基本信息 */}
                {selectedApplet && renderBasicInfo()}

                {/* 版本信息 */}
                {selectedApplet && renderVersionInfo()}

                {/* 代码模板 */}
                {selectedApplet && renderCodeTemplates()}

                {/* 操作按钮 */}
                {selectedApplet && (
                    <Card title="操作" className={`${styles.card} ${styles.operationCard}`}>
                        <Space className={styles.operationButtons}>
                            <Button
                                type="secondary"
                                icon={<IconUserAdd />}
                                onClick={() => setTesterModalVisible(true)}
                            >
                                添加体验者
                            </Button>

                            {isTrialMiniprogram(selectedApplet) && (
                                <Button
                                    type="primary"
                                    icon={<IconConvert />}
                                    onClick={() => setBetaConversionModalVisible(true)}
                                >
                                    小程序转正
                                </Button>
                            )}
                        </Space>
                    </Card>
                )}
            </div>

            {/* 提交代码模态框 */}
            <Modal
                title="提交代码升级"
                visible={submitModalVisible}
                onOk={handleSubmitCode}
                onCancel={() => setSubmitModalVisible(false)}
                confirmLoading={submitLoading}
                width={600}
                className={styles.submitModal}
            >
                <div className={styles.formField}>
                    <label className={styles.label}>选择版本</label>
                    <Select
                        placeholder="请选择代码版本"
                        value={submitForm.template_id}
                        onChange={(value) => setSubmitForm(prev => ({ ...prev, template_id: value }))}
                        className={styles.input}
                    >
                        {codeTemplates.map(template => (
                            <Select.Option key={template.TemplateId} value={template.TemplateId}>
                                {template.UserVersion} - {template.UserDesc}
                            </Select.Option>
                        ))}
                    </Select>
                </div>

                <div className={styles.formField}>
                    <label className={styles.label}>版本描述</label>
                    <Input
                        placeholder="请输入版本描述"
                        value={submitForm.user_desc}
                        onChange={(value) => setSubmitForm(prev => ({ ...prev, user_desc: value }))}
                        className={styles.input}
                    />
                </div>

                <div className={styles.formField}>
                    <label className={styles.label}>版本号</label>
                    <Input
                        placeholder="请输入版本号，如：1.0.0"
                        value={submitForm.user_version}
                        onChange={(value) => setSubmitForm(prev => ({ ...prev, user_version: value }))}
                        className={styles.input}
                    />
                </div>

                <div className={styles.formField}>
                    <label className={styles.label}>补充信息（ext_json）</label>
                    <TextArea
                        placeholder="留空"
                        value={submitForm.ext_json}
                        onChange={(value) => setSubmitForm(prev => ({ ...prev, ext_json: value }))}
                        className={styles.textarea}
                        rows={3}
                    />
                </div>
            </Modal>

            {/* 二维码显示模态框 */}
            <Modal
                title={`${currentQrCodeType === 'trial' ? '体验版' : '正式版'}二维码`}
                visible={qrCodeModalVisible}
                onCancel={closeQrCodeModal}
                footer={null}
                width={500}
                className={styles.qrCodeModal}
            >
                {qrCodeData && (
                    <div className={styles.qrCodeContent}>
                        <Image
                            src={qrCodeData}
                            alt="小程序二维码"
                            width="100%"
                            height="auto"
                            preview={false}
                        />
                        <div className={styles.qrCodeInfo}>
                            <Typography.Text type="secondary">
                                小程序：{selectedApplet?.public_name}
                            </Typography.Text>
                            <Typography.Text type="secondary">
                                版本：{currentQrCodeType === 'trial' ? '体验版' : '正式版'}
                            </Typography.Text>
                        </div>
                    </div>
                )}
            </Modal>

            {/* 添加体验者模态框 */}
            <Modal
                title="添加体验者"
                visible={testerModalVisible}
                onOk={handleBindTester}
                onCancel={() => setTesterModalVisible(false)}
                confirmLoading={testerLoading}
                width={500}
                className={styles.testerModal}
            >
                <div className={styles.formField}>
                    <label className={styles.label}>微信号</label>
                    <Input
                        placeholder="请输入微信号"
                        value={testerForm.wxid}
                        onChange={(value) => setTesterForm(prev => ({ ...prev, wxid: value }))}
                        className={styles.input}
                    />
                </div>
                <div className={styles.formField}>
                    <Typography.Text type="secondary" size="small">
                        添加体验者后，该用户可以通过体验版二维码体验小程序
                    </Typography.Text>
                </div>
            </Modal>

            {/* 名称编辑模态框 */}
            <Modal
                title="修改小程序名称"
                visible={nameEditModalVisible}
                onCancel={() => {
                    setNameEditModalVisible(false);
                    setNameForm({ name: '', license: '', id_card: '', naming_other_stuff_1: '' });
                    setNameCheckResult(null);
                }}
                footer={null}
                width={600}
                className={styles.editModal}
            >
                <div className={styles.formField}>
                    <label className={styles.label}>小程序名称 <span style={{ color: 'red' }}>*</span></label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Input
                            placeholder="请输入小程序名称（4-30个字符）"
                            value={nameForm.name}
                            onChange={(value) => setNameForm(prev => ({ ...prev, name: value }))}
                            className={styles.input}
                            style={{ flex: 1 }}
                        />
                        <Button
                            type="secondary"
                            loading={nameCheckLoading}
                            onClick={() => checkXcxName(nameForm.name)}
                            disabled={!nameForm.name.trim()}
                        >
                            检查名称
                        </Button>
                    </div>
                    <Typography.Text type="secondary" size="small">
                        名称长度应在4-30个字符之间
                    </Typography.Text>
                    <br />
                    <Typography.Text type="secondary" size="small">
                        自己注册的小程序无法在这里改名，需要登录微信公众后台修改
                    </Typography.Text>
                </div>

                {/* 名称检查结果 */}
                {nameCheckResult && (
                    <div className={styles.formField}>
                        <div style={{
                            padding: '12px',
                            borderRadius: '6px',
                            backgroundColor: nameCheckResult.hit_condition === 0 ? '#f6ffed' : '#fff7e6',
                            border: `1px solid ${nameCheckResult.hit_condition === 0 ? '#b7eb8f' : '#ffd591'}`
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                <Tag color={nameCheckResult.hit_condition === 0 ? 'green' : 'orange'} size="small">
                                    {nameCheckResult.hit_condition === 0 ? '名称可用' : '需要额外材料'}
                                </Tag>
                            </div>
                            {nameCheckResult.tip_info && (
                                <Typography.Text type="secondary" size="small">
                                    {nameCheckResult.tip_info}
                                </Typography.Text>
                            )}
                        </div>
                    </div>
                )}

                {/* 补充材料 */}
                {nameCheckResult?.hit_condition === 1 && (
                    <>
                        {basicInfo?.principal_type === 1 && (
                            <div className={styles.formField}>
                                <label className={styles.label}>营业执照 <span style={{ color: 'red' }}>*</span></label>
                                <ImageSelector
                                    value={nameForm.license}
                                    onChange={(url) => setNameForm(prev => ({ ...prev, license: url }))}
                                    width="100%"
                                    previewSize={120}
                                    showPreview={true}
                                />
                                <Typography.Text type="secondary" size="small" style={{ display: 'block', marginTop: '8px' }}>
                                    企业类型必须提供营业执照，支持 JPG、PNG 格式
                                </Typography.Text>
                            </div>
                        )}

                        {basicInfo?.principal_type === 0 && (
                            <div className={styles.formField}>
                                <label className={styles.label}>身份证 <span style={{ color: 'red' }}>*</span></label>
                                <ImageSelector
                                    value={nameForm.id_card}
                                    onChange={(url) => setNameForm(prev => ({ ...prev, id_card: url }))}
                                    width="100%"
                                    previewSize={120}
                                    showPreview={true}
                                />
                                <Typography.Text type="secondary" size="small" style={{ display: 'block', marginTop: '8px' }}>
                                    个人类型必须提供身份证，支持 JPG、PNG 格式
                                </Typography.Text>
                            </div>
                        )}

                        <div className={styles.formField}>
                            <label className={styles.label}>其他补充材料</label>
                            <ImageSelector
                                value={nameForm.naming_other_stuff_1}
                                onChange={(url) => setNameForm(prev => ({ ...prev, naming_other_stuff_1: url }))}
                                width="100%"
                                previewSize={120}
                                showPreview={true}
                            />
                            <Typography.Text type="secondary" size="small" style={{ display: 'block', marginTop: '8px' }}>
                                可选择上传其他补充材料，支持 JPG、PNG 格式
                            </Typography.Text>
                        </div>
                    </>
                )}

                <div className={styles.formField}>
                    <Space>
                        <Button
                            type="primary"
                            loading={editLoading}
                            onClick={setXcxName}
                            disabled={
                                !nameCheckResult ||
                                !nameForm.name.trim() ||
                                (nameCheckResult?.hit_condition === 1 && basicInfo?.principal_type === 1 && !nameForm.license) ||
                                (nameCheckResult?.hit_condition === 1 && basicInfo?.principal_type === 0 && !nameForm.id_card)
                            }
                        >
                            确认修改
                        </Button>
                        <Button
                            onClick={() => {
                                setNameEditModalVisible(false);
                                setNameForm({ name: '', license: '', id_card: '', naming_other_stuff_1: '' });
                                setNameCheckResult(null);
                            }}
                        >
                            取消
                        </Button>
                    </Space>
                </div>

                {basicInfo?.nickname_info && (
                    <div className={styles.formField}>
                        <Typography.Text type="secondary" size="small">
                            本年已修改 {basicInfo.nickname_info.modify_used_count} 次，
                            剩余 {basicInfo.nickname_info.modify_quota - basicInfo.nickname_info.modify_used_count} 次
                        </Typography.Text>
                    </div>
                )}
            </Modal>

            {/* 简介编辑模态框 */}
            <Modal
                title="修改功能介绍"
                visible={briefEditModalVisible}
                onOk={setXcxBrief}
                onCancel={() => {
                    setBriefEditModalVisible(false);
                    setBriefForm({ brief: '' });
                }}
                confirmLoading={editLoading}
                width={500}
                className={styles.editModal}
            >
                <div className={styles.formField}>
                    <label className={styles.label}>功能介绍 <span style={{ color: 'red' }}>*</span></label>
                    <TextArea
                        placeholder="请输入功能介绍（4-120个字符）"
                        value={briefForm.brief}
                        onChange={(value) => setBriefForm(prev => ({ ...prev, brief: value }))}
                        className={styles.textarea}
                        rows={4}
                        maxLength={120}
                        showClear
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                        <Typography.Text type="secondary" size="small">
                            功能介绍长度应在4-120个字符之间
                        </Typography.Text>
                    </div>
                </div>

                {basicInfo?.signature_info && (
                    <div className={styles.formField}>
                        <Typography.Text type="secondary" size="small">
                            本月已修改 {basicInfo.signature_info.modify_used_count} 次，
                            剩余 {basicInfo.signature_info.modify_quota - basicInfo.signature_info.modify_used_count} 次
                        </Typography.Text>
                    </div>
                )}
            </Modal>

            {/* 头像编辑模态框 */}
            <Modal
                title="修改小程序头像"
                visible={avatarEditModalVisible}
                onOk={setXcxAvatar}
                onCancel={() => {
                    setAvatarEditModalVisible(false);
                    setAvatarForm({ avatar: '', x1: '0', y1: '0', x2: '1', y2: '1' });
                }}
                confirmLoading={editLoading}
                width={600}
                className={styles.editModal}
            >
                <Space vertical spacing="medium" style={{ width: '100%' }}>
                    <div className={styles.formField}>
                        <label className={styles.label}>上传头像 <span style={{ color: 'red' }}>*</span></label>
                        <ImageSelector
                            value={avatarForm.avatar}
                            onChange={(url) => {
                                setAvatarForm(prev => ({ ...prev, avatar: url }));
                            }}
                            width="100%"
                            previewSize={100}
                            showPreview={true}
                        />
                        <Typography.Text type="secondary" size="small" style={{ display: 'block', marginTop: '8px' }}>
                            支持 JPG、PNG 格式，头像需要是正方形比例
                        </Typography.Text>
                    </div>

                    {/* 裁剪参数 */}
                    {avatarForm.avatar && (
                        <div className={styles.formField}>
                            <label className={styles.label}>裁剪参数</label>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                                <div>
                                    <Typography.Text size="small">左上角 X</Typography.Text>
                                    <Input
                                        type="number"
                                        min="0"
                                        max="1"
                                        step="0.01"
                                        value={avatarForm.x1}
                                        onChange={(value) => setAvatarForm(prev => ({ ...prev, x1: value }))}
                                        placeholder="0-1"
                                    />
                                </div>
                                <div>
                                    <Typography.Text size="small">左上角 Y</Typography.Text>
                                    <Input
                                        type="number"
                                        min="0"
                                        max="1"
                                        step="0.01"
                                        value={avatarForm.y1}
                                        onChange={(value) => setAvatarForm(prev => ({ ...prev, y1: value }))}
                                        placeholder="0-1"
                                    />
                                </div>
                                <div>
                                    <Typography.Text size="small">右下角 X</Typography.Text>
                                    <Input
                                        type="number"
                                        min="0"
                                        max="1"
                                        step="0.01"
                                        value={avatarForm.x2}
                                        onChange={(value) => setAvatarForm(prev => ({ ...prev, x2: value }))}
                                        placeholder="0-1"
                                    />
                                </div>
                                <div>
                                    <Typography.Text size="small">右下角 Y</Typography.Text>
                                    <Input
                                        type="number"
                                        min="0"
                                        max="1"
                                        step="0.01"
                                        value={avatarForm.y2}
                                        onChange={(value) => setAvatarForm(prev => ({ ...prev, y2: value }))}
                                        placeholder="0-1"
                                    />
                                </div>
                            </div>
                            <Typography.Text type="secondary" size="small" style={{ display: 'block', marginTop: '8px' }}>
                                裁剪参数范围为 0-1，表示在原图中的相对位置
                            </Typography.Text>
                        </div>
                    )}

                    {basicInfo?.head_image_info && (
                        <div className={styles.formField}>
                            <Typography.Text type="secondary" size="small">
                                本年已修改 {basicInfo.head_image_info.modify_used_count} 次，
                                剩余 {basicInfo.head_image_info.modify_quota - basicInfo.head_image_info.modify_used_count} 次
                            </Typography.Text>
                        </div>
                    )}
                </Space>
            </Modal>

            {/* 类目管理模态框 */}
            <Modal
                title="添加小程序类目"
                visible={categoryModalVisible}
                onOk={addCategory}
                onCancel={() => {
                    setCategoryModalVisible(false);
                    setCategoryForm({ first_id: '', second_id: '' });
                }}
                confirmLoading={categoryActionLoading}
                width={600}
                className={styles.categoryModal}
            >
                <Space vertical spacing="medium" style={{ width: '100%' }}>
                    <div className={styles.formField}>
                        <label className={styles.label}>一级类目 <span style={{ color: 'red' }}>*</span></label>
                        <Select
                            placeholder="请选择一级类目"
                            value={categoryForm.first_id}
                            onChange={(value) => {
                                setCategoryForm(prev => ({
                                    ...prev,
                                    first_id: value,
                                    second_id: '' // 重置二级类目
                                }));
                            }}
                            className={styles.input}
                            loading={categoryLoading}
                        >
                            {getFirstCategories().map(category => (
                                <Select.Option key={category.id} value={category.id.toString()}>
                                    <div>
                                        <div>{category.name}</div>
                                        {category.sensitive_type === 1 && (
                                            <Tag color="orange" size="small">敏感类目</Tag>
                                        )}
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    </div>

                    <div className={styles.formField}>
                        <label className={styles.label}>二级类目 <span style={{ color: 'red' }}>*</span></label>
                        <Select
                            placeholder="请先选择一级类目"
                            value={categoryForm.second_id}
                            onChange={(value) => setCategoryForm(prev => ({ ...prev, second_id: value }))}
                            className={styles.input}
                            disabled={!categoryForm.first_id}
                            loading={categoryLoading}
                        >
                            {getSecondCategories(categoryForm.first_id).map(category => (
                                <Select.Option key={category.id} value={category.id.toString()}>
                                    <div>
                                        <div>{category.name}</div>
                                        {category.sensitive_type === 1 && (
                                            <Tag color="orange" size="small">敏感类目</Tag>
                                        )}
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    </div>

                    {/* 敏感类目提示 */}
                    {categoryForm.first_id && (() => {
                        const firstCat = categoryList.find(cat => cat.id === parseInt(categoryForm.first_id));
                        const secondCat = categoryForm.second_id ? categoryList.find(cat => cat.id === parseInt(categoryForm.second_id)) : null;
                        const isSensitive = firstCat?.sensitive_type === 1 || secondCat?.sensitive_type === 1;

                        if (isSensitive) {
                            const qualify = firstCat?.qualify || secondCat?.qualify;
                            return (
                                <div className={styles.formField}>
                                    <div style={{
                                        padding: '12px',
                                        borderRadius: '6px',
                                        backgroundColor: '#fff7e6',
                                        border: '1px solid #ffd591'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                            <IconAlertTriangle style={{ color: '#fa8c16', marginRight: '8px' }} />
                                            <Typography.Text strong>敏感类目需要额外资质</Typography.Text>
                                        </div>
                                        {qualify && qualify.exter_list && qualify.exter_list.length > 0 && (
                                            <div>
                                                <Typography.Text type="secondary" size="small">
                                                    需要提供的资质材料：
                                                </Typography.Text>
                                                {qualify.exter_list.map((exter, exterIndex) => (
                                                    <div key={exterIndex} style={{ marginTop: '8px' }}>
                                                        {exter.inner_list && exter.inner_list.map((inner, innerIndex) => (
                                                            <div key={innerIndex} style={{ marginLeft: '16px' }}>
                                                                <Typography.Text size="small">• {inner.name}</Typography.Text>
                                                                {inner.url && (
                                                                    <Typography.Link
                                                                        href={inner.url}
                                                                        target="_blank"
                                                                        size="small"
                                                                        style={{ marginLeft: '8px' }}
                                                                    >
                                                                        查看示例
                                                                    </Typography.Link>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                                {qualify.remark && (
                                                    <Typography.Text type="secondary" size="small" style={{ display: 'block', marginTop: '8px' }}>
                                                        备注：{qualify.remark}
                                                    </Typography.Text>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        }
                        return null;
                    })()}

                    <div className={styles.formField}>
                        <Typography.Text type="secondary" size="small">
                            添加类目后需要等待微信审核通过才能正式生效
                        </Typography.Text>
                    </div>
                </Space>
            </Modal>

            {/* 提交审核模态框 */}
            <Modal
                title="提交版本审核"
                visible={auditModalVisible}
                onOk={commitAudit}
                onCancel={() => {
                    setAuditModalVisible(false);
                    setAuditForm({
                        first_id: '',
                        first_class: '',
                        second_id: '',
                        second_class: ''
                    });
                }}
                confirmLoading={auditLoading}
                width={600}
                className={styles.auditModal}
            >
                <div className={styles.formField}>
                    <Typography.Text type="secondary" size="small" style={{ display: 'block', marginBottom: '16px' }}>
                        请选择一个已添加的类目来提交审核，审核通过后即可发布正式版。
                    </Typography.Text>
                    <Typography.Text type="secondary" size="small" style={{ display: 'block', marginBottom: '16px' }}>
                        提审前请先确保名称，头像，简介，类目已经设置
                    </Typography.Text>
                </div>

                <div className={styles.formField}>
                    <label className={styles.label}>选择类目 <span style={{ color: 'red' }}>*</span></label>
                    <Select
                        placeholder="请选择已添加的类目"
                        value={auditForm.first_id ? `${auditForm.first_id}_${auditForm.second_id}` : ''}
                        onChange={(value) => {
                            if (value) {
                                const [firstId, secondId] = value.split('_');
                                const category = categoryInfo?.find(cat =>
                                    cat.first === firstId && cat.second === secondId
                                );
                                if (category) {
                                    setAuditForm({
                                        first_id: firstId,
                                        first_class: category.first_name,
                                        second_id: secondId,
                                        second_class: category.second_name
                                    });
                                }
                            }
                        }}
                        className={styles.input}
                    >
                        {categoryInfo && categoryInfo.length > 0 ? (
                            categoryInfo
                                .filter(category => category.audit_status === 3) // 只显示审核通过的类目
                                .map((category, index) => (
                                    <Select.Option
                                        key={index}
                                        value={`${category.first}_${category.second}`}
                                    >
                                        <div>
                                            <div>{category.first_name} {' > '} {category.second_name}</div>
                                            <Tag color="green" size="small">已通过审核</Tag>
                                        </div>
                                    </Select.Option>
                                ))
                        ) : (
                            <Select.Option value="" disabled>
                                暂无可用类目，请先添加并通过审核
                            </Select.Option>
                        )}
                    </Select>

                    {categoryInfo && categoryInfo.filter(cat => cat.audit_status === 3).length === 0 && (
                        <div style={{
                            padding: '12px',
                            borderRadius: '6px',
                            backgroundColor: '#fff7e6',
                            border: '1px solid #ffd591',
                            marginTop: '8px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                <IconAlertTriangle style={{ color: '#fa8c16', marginRight: '8px' }} />
                                <Typography.Text strong>需要先添加类目</Typography.Text>
                            </div>
                            <Typography.Text type="secondary" size="small">
                                提交审核前，需要先添加小程序类目并通过微信审核。
                                请前往基本信息区域添加类目。
                            </Typography.Text>
                        </div>
                    )}
                </div>

                <div className={styles.formField}>
                    <div style={{
                        padding: '12px',
                        borderRadius: '6px',
                        backgroundColor: '#e6f7ff',
                        border: '1px solid #91d5ff'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                            <IconVerify style={{ color: '#1890ff', marginRight: '8px' }} />
                            <Typography.Text strong>审核说明</Typography.Text>
                        </div>
                        <Typography.Text type="secondary" size="small">
                            • 提交后将进入微信审核流程，一般需要1-7个工作日<br />
                            • 审核期间可以撤回审核重新提交<br />
                            • 审核通过后可以发布为正式版<br />
                            • 审核被拒绝时会显示具体原因
                        </Typography.Text>
                    </div>
                </div>
            </Modal>

            {/* 小程序转正模态框 */}
            <Modal
                title="小程序转正申请"
                visible={betaConversionModalVisible}
                onOk={handleCommitBeta}
                onCancel={() => {
                    setBetaConversionModalVisible(false);
                    setBetaConversionForm({
                        enterprise_name: '',
                        code_type: 1,
                        code: '',
                        legal_person_wechat: '',
                        legal_person_name: '',
                        legal_person_idcard: '',
                        component_phone: ''
                    });
                }}
                confirmLoading={betaConversionLoading}
                width={700}
                className={styles.betaConversionModal}
            >
                <div className={styles.formField}>
                    <Typography.Text type="secondary" size="small" style={{ display: 'block', marginBottom: '16px' }}>
                        试用小程序转正需要提供企业相关信息，提交后法人将收到扫脸验证通知。
                    </Typography.Text>
                </div>

                <div className={styles.formField}>
                    <label className={styles.label}>企业名称 <span style={{ color: 'red' }}>*</span></label>
                    <Input
                        placeholder="请输入企业名称"
                        value={betaConversionForm.enterprise_name}
                        onChange={(value) => setBetaConversionForm(prev => ({ ...prev, enterprise_name: value }))}
                        className={styles.input}
                    />
                </div>

                <div className={styles.formField}>
                    <label className={styles.label}>企业代码类型 <span style={{ color: 'red' }}>*</span></label>
                    <Select
                        value={betaConversionForm.code_type}
                        onChange={(value) => setBetaConversionForm(prev => ({ ...prev, code_type: value }))}
                        className={styles.input}
                    >
                        <Select.Option value={1}>统一社会信用代码(18位)</Select.Option>
                        <Select.Option value={2}>组织机构代码(9位)</Select.Option>
                        <Select.Option value={3}>营业执照注册号(15位)</Select.Option>
                    </Select>
                </div>

                <div className={styles.formField}>
                    <label className={styles.label}>企业代码 <span style={{ color: 'red' }}>*</span></label>
                    <Input
                        placeholder={betaConversionForm.code_type === 1 ? "请输入18位统一社会信用代码" :
                            betaConversionForm.code_type === 2 ? "请输入9位组织机构代码" :
                                "请输入15位营业执照注册号"}
                        value={betaConversionForm.code}
                        onChange={(value) => setBetaConversionForm(prev => ({ ...prev, code: value }))}
                        className={styles.input}
                        maxLength={betaConversionForm.code_type === 1 ? 18 : betaConversionForm.code_type === 2 ? 9 : 15}
                    />
                </div>

                <div className={styles.formField}>
                    <label className={styles.label}>法人微信 <span style={{ color: 'red' }}>*</span></label>
                    <Input
                        placeholder="请输入法人微信号"
                        value={betaConversionForm.legal_person_wechat}
                        onChange={(value) => setBetaConversionForm(prev => ({ ...prev, legal_person_wechat: value }))}
                        className={styles.input}
                    />
                </div>

                <div className={styles.formField}>
                    <label className={styles.label}>法人姓名 <span style={{ color: 'red' }}>*</span></label>
                    <Input
                        placeholder="请输入法人姓名"
                        value={betaConversionForm.legal_person_name}
                        onChange={(value) => setBetaConversionForm(prev => ({ ...prev, legal_person_name: value }))}
                        className={styles.input}
                    />
                </div>

                <div className={styles.formField}>
                    <label className={styles.label}>法人身份证 <span style={{ color: 'red' }}>*</span></label>
                    <Input
                        placeholder="请输入法人身份证号"
                        value={betaConversionForm.legal_person_idcard}
                        onChange={(value) => setBetaConversionForm(prev => ({ ...prev, legal_person_idcard: value }))}
                        className={styles.input}
                        maxLength={18}
                    />
                </div>

                <div className={styles.formField}>
                    <label className={styles.label}>联系电话 <span style={{ color: 'red' }}>*</span></label>
                    <Input
                        placeholder="请输入联系电话"
                        value={betaConversionForm.component_phone}
                        onChange={(value) => setBetaConversionForm(prev => ({ ...prev, component_phone: value }))}
                        className={styles.input}
                        maxLength={11}
                    />
                </div>

                <div className={styles.formField}>
                    <div style={{
                        padding: '12px',
                        borderRadius: '6px',
                        backgroundColor: '#e6f7ff',
                        border: '1px solid #91d5ff'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                            <IconVerify style={{ color: '#1890ff', marginRight: '8px' }} />
                            <Typography.Text strong>转正说明</Typography.Text>
                        </div>
                        <Typography.Text type="secondary" size="small">
                            • 提交申请后，法人将收到扫脸验证的模板消息通知<br />
                            • 法人需要在微信中完成身份验证<br />
                            • 验证通过后，试用小程序将转为正式小程序<br />
                            • 转正后可以正常发布和运营小程序
                        </Typography.Text>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default XcxVersionManager;
